import { useState } from 'react'
import { X, Loader2 } from 'lucide-react'
import { useApp } from '@/context/AppContext'
import type { Project } from '@/lib/types'

interface Props {
  project: Project
  onClose: () => void
}

const inputClass =
  'w-full px-3.5 py-2.5 bg-[#F8F7F3] border border-[#E0DFD8] rounded-xl text-[#111110] text-sm font-body placeholder:text-[#C8C7BF] focus:outline-none focus:border-[#111110] focus:bg-white transition-all'

export function EditProjectModal({ project, onClose }: Props) {
  const { updateProject } = useApp()
  const [name, setName] = useState(project.name)
  const [description, setDescription] = useState(project.description)
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || saving) return
    setSaving(true)
    try {
      await updateProject(project.id, {
        name: name.trim(),
        description: description.trim(),
      })
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-fade-in" onClick={onClose} />

      <div className="relative w-full max-w-[480px] bg-white border border-[#E0DFD8] rounded-2xl shadow-2xl shadow-black/15 animate-slide-up">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#F2F1EC]">
          <h2 className="font-display text-lg font-bold text-[#111110]">Edit Project</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-[#F2F1EC] text-[#AEADA8] hover:text-[#111110] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">
              Nama Project <span className="text-[#DC2626] font-normal text-xs">*wajib</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">
              Deskripsi <span className="text-[#AEADA8] font-normal text-xs">opsional</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tujuan dan scope project ini..."
              rows={3}
              className={`${inputClass} resize-none leading-relaxed`}
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-body text-[#8A8A85] hover:text-[#111110] transition-colors"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={!name.trim() || saving}
              className="flex items-center gap-2 px-5 py-2 bg-[#111110] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2A2A28] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Simpan
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
